import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, addDoc, query,
  where, Timestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { Spin } from "antd";
import dayjs from "dayjs";

const PRICE_PER_15 = 1;
const MAX_DAILY    = 45;
const FREE_WEEKLY  = 15;

export default function BoothPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const [loading, setLoading]           = useState(true);
  const [fullName, setFullName]         = useState("");
  const [contactDocId, setContactDocId] = useState(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [hasSession, setHasSession]     = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null); // minutes left in session

  const [boothBusy, setBoothBusy]           = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [allTodayBookings, setAllTodayBookings] = useState([]);

  const [minutesUsedToday, setMinutesUsedToday] = useState(0);
  const [minutesUsedWeek, setMinutesUsedWeek]   = useState(0);
  const [freeMinutesLeft, setFreeMinutesLeft]   = useState(0);

  // Form
  const [duration, setDuration]     = useState(null);
  const [alone, setAlone]           = useState(null); // true = alone, false = with someone
  const [partner, setPartner]       = useState(null); // selected visiteur doc
  const [activeVisiteurs, setActiveVisiteurs] = useState([]);
  const [selectedTime, setSelectedTime]       = useState(null); // HH:mm string
  const [availableSlots, setAvailableSlots]   = useState([]);

  const [booking, setBooking]   = useState(false);
  const [booked, setBooked]     = useState(null);
  const [error, setError]       = useState("");

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };
  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const init = async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }

      // 1 — Contact
      try {
        const cSnap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
        if (!cSnap.empty) {
          setFullName(cSnap.docs[0].data().fullname || cSnap.docs[0].data().name || "");
          setContactDocId(cSnap.docs[0].id);
        }
      } catch {}

      // 2 — Subscriber
      try {
        const mSnap = await getDocs(query(collection(db, "members"), where("FirebaseId", "==", user.uid)));
        setIsSubscriber(!mSnap.empty);
      } catch {}

      // 3 — Active session + time left
      try {
        const todayStart = dayjs().startOf("day").toDate();
        const vSnap = await getDocs(query(
          collection(db, "visiteur"),
          where("FirebaseId", "==", user.uid),
          where("sortie", "==", false),
          where("startTime", ">=", Timestamp.fromDate(todayStart))
        ));
        if (!vSnap.empty) {
          setHasSession(true);
          const d = vSnap.docs[0].data();
          // hours field = total hours purchased, startTime = when session started
          const startTime = d.startTime?.toDate();
          const totalMinutes = (d.hours || 0) * 60;
          if (startTime && totalMinutes > 0) {
            const elapsed = (Date.now() - startTime.getTime()) / 60000;
            const left = Math.max(0, Math.round(totalMinutes - elapsed));
            setSessionTimeLeft(left);
          }
        }
      } catch {}

      // 4 — All active visiteurs (excluding current user) for partner selection
      try {
        const todayStart = dayjs().startOf("day").toDate();
        const allV = await getDocs(query(
          collection(db, "visiteur"),
          where("sortie", "==", false),
          where("startTime", ">=", Timestamp.fromDate(todayStart))
        ));
        const others = allV.docs
          .filter(d => d.data().FirebaseId !== user.uid)
          .map(d => ({ id: d.id, ...d.data() }));
        setActiveVisiteurs(others);
      } catch {}

      // 5 — Booth busy now
      try {
        const now = new Date();
        const bSnap = await getDocs(query(
          collection(db, "booth_bookings"),
          where("status", "==", "active"),
          where("endTime", ">", Timestamp.fromDate(now))
        ));
        if (!bSnap.empty) {
          setBoothBusy(true);
          setCurrentBooking(bSnap.docs[0].data());
        }
      } catch {}

      // 6 — All today's booth bookings (for slot availability)
      try {
        const todayStart = dayjs().startOf("day").toDate();
        const todayEnd   = dayjs().endOf("day").toDate();
        const allB = await getDocs(query(
          collection(db, "booth_bookings"),
          where("startTime", ">=", Timestamp.fromDate(todayStart)),
          where("startTime", "<=", Timestamp.fromDate(todayEnd))
        ));
        setAllTodayBookings(allB.docs.map(d => ({ id: d.id, ...d.data() })));

        // User's usage today
        const myBookings = allB.docs.filter(d => d.data().userId === user.uid);
        const usedToday = myBookings.reduce((s, d) => s + (d.data().duration || 0), 0);
        setMinutesUsedToday(usedToday);
      } catch {}

      // 7 — Free quota this week (Mon-Sun)
      try {
        const now2 = new Date();
        const day = now2.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const weekStart = new Date(now2);
        weekStart.setDate(now2.getDate() + diffToMonday);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const wSnap = await getDocs(query(
          collection(db, "booth_bookings"),
          where("userId", "==", user.uid),
          where("startTime", ">=", Timestamp.fromDate(weekStart)),
          where("startTime", "<=", Timestamp.fromDate(weekEnd)),
          where("freeMinutes", ">", 0)
        ));
        const usedFree = wSnap.docs.reduce((s, d) => s + (d.data().freeMinutes || 0), 0);
        setMinutesUsedWeek(usedFree);
        setFreeMinutesLeft(Math.max(0, FREE_WEEKLY - usedFree));
      } catch {}

      setLoading(false);
    };
    init();
  }, []);

  // Generate available time slots when duration changes
  useEffect(() => {
    if (!duration) { setAvailableSlots([]); setSelectedTime(null); return; }
    generateSlots(duration);
  }, [duration, allTodayBookings]);

  const generateSlots = (dur) => {
    const now = dayjs();
    const slots = [];
    // Generate slots from next full 15-min mark until midnight
    let cursor = now.minute(Math.ceil(now.minute() / 15) * 15).second(0).millisecond(0);
    if (cursor.isBefore(now)) cursor = cursor.add(15, "minute");
    const midnight = dayjs().endOf("day");

    while (cursor.add(dur, "minute").isBefore(midnight) || cursor.add(dur, "minute").isSame(midnight)) {
      const slotStart = cursor.toDate();
      const slotEnd   = cursor.add(dur, "minute").toDate();

      // Check no overlap with existing bookings
      const hasOverlap = allTodayBookings.some(b => {
        const bStart = b.startTime?.toDate?.() || new Date(b.startTime);
        const bEnd   = b.endTime?.toDate?.()   || new Date(b.endTime);
        return slotStart < bEnd && slotEnd > bStart;
      });

      if (!hasOverlap) {
        slots.push(cursor.format("HH:mm"));
      }
      cursor = cursor.add(15, "minute");
    }
    setAvailableSlots(slots);
    setSelectedTime(null);
  };

  const calcCost = (dur) => {
    if (!dur) return { free: 0, paid: dur, cost: 0 };
    let free = 0, paid = dur;
    if (isSubscriber && freeMinutesLeft > 0) {
      free = Math.min(freeMinutesLeft, dur);
      paid = dur - free;
    }
    return { free, paid, cost: (paid / 15) * PRICE_PER_15 };
  };

  const remainingToday = MAX_DAILY - minutesUsedToday;
  // Also cap by session time left
  const maxAvailable = sessionTimeLeft !== null
    ? Math.min(remainingToday, sessionTimeLeft)
    : remainingToday;

  const handleBook = async () => {
    setError("");
    if (!duration || !selectedTime) return;
    if (!hasSession) { setError("Vous devez avoir une session active."); return; }
    if (boothBusy) { setError("Le booth est actuellement occupé."); return; }

    setBooking(true);
    try {
      const user = auth.currentUser;
      const [h, m] = selectedTime.split(":").map(Number);
      const startTime = dayjs().hour(h).minute(m).second(0).millisecond(0).toDate();
      const endTime   = new Date(startTime.getTime() + duration * 60 * 1000);
      const { free, paid, cost } = calcCost(duration);

      await addDoc(collection(db, "booth_bookings"), {
        userId:        user.uid,
        userName:      fullName,
        alone:         alone === true,
        partnerId:     partner?.FirebaseId || null,
        partnerName:   partner ? (partner.name || partner.fullname || "") : null,
        duration,
        freeMinutes:   free,
        paidMinutes:   paid,
        cost,
        startTime:     Timestamp.fromDate(startTime),
        endTime:       Timestamp.fromDate(endTime),
        status:        "active",
        isSubscriber,
        createdAt:     Timestamp.fromDate(new Date()),
      });

      if (cost > 0) {
        await addDoc(collection(db, "buvette"), {
          "Date et Heure": new Date(),
          "Nom Client":    fullName,
          Produit:         `Booth — ${duration}min${partner ? ` (+ ${partner.name || partner.fullname})` : ""}`,
          Total:           cost,
          Paiement:        false,
        });
      }

      setBooked({ duration, free, paid, cost, startTime, endTime, partner });
      setBoothBusy(true);
      setMinutesUsedToday(prev => prev + duration);
      setDuration(null);
      setPartner(null);
      setAlone(null);
      setSelectedTime(null);
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la réservation.");
    }
    setBooking(false);
  };

  const canBook = duration && selectedTime && alone !== null && (alone || (!alone && partner));

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Booth</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Espace privé · 2 personnes max</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}><Spin /></div>
        ) : (
          <div style={{ padding: "16px 18px 0", display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Important notice */}
            <div style={{ background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.amberText, marginBottom: 4 }}>⚠️ Information importante</div>
              <div style={{ fontSize: 12, color: t.amberText, lineHeight: 1.6 }}>
                Le booth est un service <strong>hors abonnement</strong> — peut être retiré à tout moment. Abonnés : <strong>15 min gratuites/semaine</strong> (Lun→Dim), puis <strong>1 dt / 15 min</strong>.
              </div>
            </div>

            {/* No session */}
            {!hasSession && (
              <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 14, padding: 16, textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🚪</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.red, marginBottom: 6 }}>Pas de session active</div>
                <div style={{ fontSize: 12, color: t.text2 }}>Enregistrez-vous à l'accueil pour accéder au booth.</div>
              </div>
            )}

            {/* Booth status */}
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: boothBusy ? t.redBg : t.greenBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                  {boothBusy ? "🔴" : "🟢"}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text1 }}>Booth {boothBusy ? "Occupé" : "Disponible"}</div>
                  {boothBusy && currentBooking?.endTime && (
                    <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>
                      Libre à {dayjs(currentBooking.endTime?.toDate?.()).format("HH:mm")}
                    </div>
                  )}
                  {!boothBusy && <div style={{ fontSize: 11, color: t.green, marginTop: 2 }}>Prêt à être réservé</div>}
                </div>
              </div>
            </div>

            {/* Session time left */}
            {hasSession && sessionTimeLeft !== null && (
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: t.text2 }}>⏳ Temps restant dans ta session</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: sessionTimeLeft < 30 ? t.amber : t.green }}>{sessionTimeLeft} min</span>
                </div>
                {sessionTimeLeft < 45 && (
                  <div style={{ fontSize: 11, color: t.amberText, marginTop: 6 }}>
                    ⚠️ Tu ne peux réserver que {Math.min(sessionTimeLeft, remainingToday)} min maximum.
                  </div>
                )}
              </div>
            )}

            {/* Subscriber free quota */}
            {isSubscriber && (
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.text2, marginBottom: 10 }}>🎁 Quota gratuit cette semaine</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: t.text1 }}>Minutes gratuites restantes</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: freeMinutesLeft > 0 ? t.green : t.text3 }}>{freeMinutesLeft} min</span>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: t.bg3, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 3, background: freeMinutesLeft > 0 ? t.green : t.border, width: `${(freeMinutesLeft / FREE_WEEKLY) * 100}%` }} />
                </div>
                <div style={{ fontSize: 11, color: t.text3, marginTop: 6 }}>Semaine Lun → Dim · {minutesUsedWeek}/{FREE_WEEKLY} min utilisées</div>
              </div>
            )}

            {/* Daily usage */}
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: t.text1 }}>📅 Utilisation aujourd'hui</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: minutesUsedToday >= MAX_DAILY ? t.red : t.text1 }}>
                  {minutesUsedToday} / {MAX_DAILY} min
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: t.bg3, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 3, background: minutesUsedToday >= MAX_DAILY ? t.red : t.blue, width: `${Math.min((minutesUsedToday / MAX_DAILY) * 100, 100)}%` }} />
              </div>
            </div>

            {/* Success */}
            {booked && (
              <div style={{ background: t.greenBg, border: `1px solid ${t.green}44`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.green, marginBottom: 8 }}>✅ Booth réservé !</div>
                <div style={{ fontSize: 13, color: t.text1, lineHeight: 1.8 }}>
                  ⏱ Durée : <strong>{booked.duration} min</strong><br />
                  🕐 De <strong>{dayjs(booked.startTime).format("HH:mm")}</strong> à <strong>{dayjs(booked.endTime).format("HH:mm")}</strong><br />
                  {booked.partner && <>👥 Avec : <strong>{booked.partner.name || booked.partner.fullname}</strong><br /></>}
                  {booked.free > 0 && <>🎁 Gratuit : <strong>{booked.free} min</strong><br /></>}
                  {booked.cost > 0 ? <>💳 À payer : <strong>{booked.cost} dt</strong> (compte buvette)</> : <>💳 <strong>Gratuit ✅</strong></>}
                </div>
              </div>
            )}

            {/* Booking form */}
            {hasSession && minutesUsedToday < MAX_DAILY && !booked && (
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text1 }}>📋 Réserver le booth</div>

                {/* Duration */}
                <div>
                  <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 8 }}>Durée souhaitée</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[15, 30, 45].map(d => {
                      const disabled = d > maxAvailable;
                      const { free, cost } = calcCost(d);
                      return (
                        <button key={d} onClick={() => !disabled && setDuration(d)} disabled={disabled}
                          style={{
                            flex: 1, padding: "10px 6px", borderRadius: 12, fontSize: 12,
                            cursor: disabled ? "not-allowed" : "pointer",
                            border: `1.5px solid ${duration === d ? t.blue : t.border}`,
                            background: duration === d ? t.blueBg : t.bg3,
                            color: disabled ? t.text3 : duration === d ? t.blueText : t.text1,
                            opacity: disabled ? 0.35 : 1,
                          }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{d} min</div>
                          <div style={{ fontSize: 10, marginTop: 3, color: duration === d ? t.blueText : t.text3 }}>
                            {disabled ? "Indisponible" : cost === 0 ? "🎁 Gratuit" : `${cost} dt`}
                            {!disabled && free > 0 && cost > 0 && ` (${free}m offert)`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {maxAvailable < 45 && (
                    <div style={{ fontSize: 11, color: t.amberText, marginTop: 6 }}>
                      ⚠️ Maximum disponible : {maxAvailable} min
                    </div>
                  )}
                </div>

                {/* Time picker */}
                {duration && (
                  <div>
                    <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 8 }}>
                      🕐 Heure de début
                    </label>
                    {availableSlots.length === 0 ? (
                      <div style={{ fontSize: 12, color: t.red, background: t.redBg, borderRadius: 10, padding: "10px 12px" }}>
                        Aucun créneau disponible pour cette durée aujourd'hui.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {availableSlots.map(slot => (
                          <button key={slot} onClick={() => setSelectedTime(slot)} style={{
                            padding: "7px 14px", borderRadius: 20, fontSize: 12,
                            border: `1.5px solid ${selectedTime === slot ? t.blue : t.border}`,
                            background: selectedTime === slot ? t.blueBg : t.bg3,
                            color: selectedTime === slot ? t.blueText : t.text2,
                            fontWeight: selectedTime === slot ? 600 : 400, cursor: "pointer",
                          }}>
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Alone or with someone */}
                {duration && selectedTime && (
                  <div>
                    <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 8 }}>👥 Participants</label>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <button onClick={() => { setAlone(true); setPartner(null); }} style={{
                        flex: 1, padding: "10px 8px", borderRadius: 10, fontSize: 13,
                        border: `1.5px solid ${alone === true ? t.blue : t.border}`,
                        background: alone === true ? t.blueBg : t.bg3,
                        color: alone === true ? t.blueText : t.text2,
                        fontWeight: alone === true ? 600 : 400, cursor: "pointer",
                      }}>🙋 Je serai seul(e)</button>
                      <button onClick={() => setAlone(false)} style={{
                        flex: 1, padding: "10px 8px", borderRadius: 10, fontSize: 13,
                        border: `1.5px solid ${alone === false ? t.blue : t.border}`,
                        background: alone === false ? t.blueBg : t.bg3,
                        color: alone === false ? t.blueText : t.text2,
                        fontWeight: alone === false ? 600 : 400, cursor: "pointer",
                      }}>👫 Avec quelqu'un</button>
                    </div>

                    {/* Partner selector */}
                    {alone === false && (
                      <div>
                        <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 8 }}>
                          Choisir le 2ème participant (membres présents maintenant)
                        </label>
                        {activeVisiteurs.length === 0 ? (
                          <div style={{ fontSize: 12, color: t.text3, background: t.bg3, borderRadius: 10, padding: "10px 12px" }}>
                            Aucun autre membre avec une session active en ce moment.
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 200, overflowY: "auto" }}>
                            {activeVisiteurs.map(v => {
                              const name = v.name || v.fullname || v.FirebaseId;
                              const isSelected = partner?.id === v.id;
                              return (
                                <div key={v.id} onClick={() => setPartner(isSelected ? null : v)}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                                    borderRadius: 10, cursor: "pointer",
                                    border: `1.5px solid ${isSelected ? t.blue : t.border}`,
                                    background: isSelected ? t.blueBg : t.bg3,
                                  }}>
                                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: isSelected ? t.blue : t.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: isSelected ? "#fff" : t.text3, fontWeight: 700, flexShrink: 0 }}>
                                    {name?.[0]?.toUpperCase() || "?"}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? t.blueText : t.text1 }}>{name}</div>
                                    <div style={{ fontSize: 10, color: t.text3 }}>Table {v.table || "—"}</div>
                                  </div>
                                  {isSelected && <div style={{ marginLeft: "auto", color: t.blue, fontSize: 16 }}>✓</div>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Cost summary */}
                {duration && selectedTime && alone !== null && (
                  (() => {
                    const { free, paid, cost } = calcCost(duration);
                    return (
                      <div style={{ background: t.bg3, borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, color: t.text2, marginBottom: 6 }}>💰 Récapitulatif</div>
                        {free > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}><span style={{ color: t.green }}>🎁 Minutes gratuites</span><span style={{ color: t.green, fontWeight: 600 }}>{free} min</span></div>}
                        {paid > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}><span style={{ color: t.text2 }}>⏱ Minutes payantes</span><span style={{ color: t.text1, fontWeight: 600 }}>{paid} min</span></div>}
                        <div style={{ height: 1, background: t.border, margin: "6px 0" }} />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}>
                          <span style={{ color: t.text1 }}>Total</span>
                          <span style={{ color: cost === 0 ? t.green : t.blue }}>{cost === 0 ? "Gratuit 🎁" : `${cost} dt`}</span>
                        </div>
                        {cost > 0 && <div style={{ fontSize: 10, color: t.text3, marginTop: 4 }}>Ajouté à votre compte buvette</div>}
                      </div>
                    );
                  })()
                )}

                {error && (
                  <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 10, padding: "10px 12px", fontSize: 12, color: t.red }}>
                    ⚠️ {error}
                  </div>
                )}

                <button onClick={handleBook} disabled={!canBook || booking}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: !canBook || booking ? t.bg3 : t.blue, color: !canBook || booking ? t.text3 : "#fff", fontSize: 14, fontWeight: 600, cursor: !canBook ? "not-allowed" : "pointer", opacity: !canBook ? 0.5 : 1 }}>
                  {booking ? "Réservation..." : "🚪 Confirmer la réservation"}
                </button>
              </div>
            )}

            {/* Daily limit reached */}
            {hasSession && minutesUsedToday >= MAX_DAILY && (
              <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 14, padding: 16, textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⛔</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.red }}>Limite journalière atteinte</div>
                <div style={{ fontSize: 12, color: t.text2, marginTop: 4 }}>Vous avez utilisé vos {MAX_DAILY} minutes de booth pour aujourd'hui.</div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

const theme = {
  light: { bg:"#f8fafc",bg3:"#f1f5f9",border:"#e2e8f0",text1:"#0f172a",text2:"#64748b",text3:"#94a3b8",blue:"#2563eb",blueBg:"#eff6ff",blueText:"#1d4ed8",green:"#16a34a",greenBg:"#f0fdf4",amber:"#d97706",amberBg:"#fffbeb",amberText:"#b45309",amberBorder:"#fde68a",red:"#dc2626",redBg:"#fef2f2",redBorder:"#fecaca",cardBg:"#ffffff" },
  dark:  { bg:"#0f172a",bg3:"#1a2740",border:"#273549",text1:"#f1f5f9",text2:"#94a3b8",text3:"#475569",blue:"#378ADD",blueBg:"#1e3a5f",blueText:"#85B7EB",green:"#4ade80",greenBg:"#052e16",amber:"#fbbf24",amberBg:"#2a1e07",amberText:"#fcd34d",amberBorder:"#78350f",red:"#f87171",redBg:"#2d1515",redBorder:"#7f1d1d",cardBg:"#1e293b" },
};

const s = {
  toggleTrack: { width:36,height:20,borderRadius:20,cursor:"pointer",position:"relative",flexShrink:0 },
  toggleKnob:  { width:14,height:14,borderRadius:"50%",position:"absolute",top:2,left:2,transition:"transform 0.2s" },
};